import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { db } from '@vibe/db/server';
import { portalSessions, portalUsers } from '@generated/models';
import { hashToken, logActivity, MAX_ATTEMPTS, newToken, requireSession, safeEqual, toSessionUser } from './auth.server';

export const getSetupState = createServerFn({ method: 'GET' })
  .validator(z.object({}).optional())
  .handler(async () => {
    const count = await db.collection(portalUsers).count();
    return { needsSetup: count === 0 };
  });

export const runInitialSetup = createServerFn({ method: 'POST' })
  .validator(z.object({}).optional())
  .handler(async () => {
    const count = await db.collection(portalUsers).count();
    if (count > 0) throw new Error('Setup has already been completed.');
    const t = newToken();
    await db.collection(portalUsers).add({
      username: 'christina.hebert',
      fullName: 'Christina Hebert',
      email: '',
      organization: 'Administration',
      role: 'admin',
      status: 'active',
      tokenHash: t.hash,
      tokenSalt: t.salt,
      failedAttempts: 0,
      libraries: ['itp', 'sd'],
      permissions: ['respondent_names', 'export_summaries', 'view_reporting', 'view_full_decisions'],
      createdAt: new Date().toISOString(),
    });
    await logActivity('christina.hebert', 'User Change', 'Initial administrator created');
    return { username: 'christina.hebert', token: t.token };
  });

export const signIn = createServerFn({ method: 'POST' })
  .validator(z.object({ username: z.string().min(1).max(80), token: z.string().min(1).max(200) }))
  .handler(async ({ data }) => {
    const username = data.username.trim().toLowerCase();
    const generic = 'The username or access token is incorrect.';
    const found = await db.collection(portalUsers).where('username', '==', username).limit(1).get();
    const user = found[0];
    if (!user) {
      await logActivity(username, 'Sign In Failed', 'Unknown username');
      throw new Error(generic);
    }
    if (user.data.status === 'locked') throw new Error('This account is locked after too many attempts. Contact an administrator.');
    if (user.data.status === 'disabled') throw new Error('This account is disabled.');
    const ok = safeEqual(hashToken(data.token.trim(), user.data.tokenSalt), user.data.tokenHash);
    const ref = db.collection(portalUsers).doc(user.id);
    if (!ok) {
      const attempts = (user.data.failedAttempts ?? 0) + 1;
      await ref.update({ failedAttempts: attempts, ...(attempts >= MAX_ATTEMPTS ? { status: 'locked' as const } : {}) });
      await logActivity(username, 'Sign In Failed', `Attempt ${attempts}`);
      if (attempts >= MAX_ATTEMPTS) throw new Error('This account is now locked after 5 failed attempts. Contact an administrator.');
      throw new Error(generic);
    }
    const now = new Date().toISOString();
    await ref.update({ failedAttempts: 0, lastLogin: now });
    const session = await db.collection(portalSessions).add({ userId: user.id, createdAt: now, lastSeen: now });
    await logActivity(username, 'Sign In');
    return { sid: session.id, user: toSessionUser(user.id, user.data) };
  });

export const getMe = createServerFn({ method: 'POST' })
  .validator(z.object({ sid: z.string() }))
  .handler(async ({ data }) => {
    const user = await requireSession(data.sid);
    return { user };
  });

export const signOut = createServerFn({ method: 'POST' })
  .validator(z.object({ sid: z.string() }))
  .handler(async ({ data }) => {
    try {
      const user = await requireSession(data.sid);
      await logActivity(user.username, 'Sign Out');
      await db.collection(portalSessions).doc(data.sid).delete();
    } catch {
      /* already ended */
    }
    return { ok: true };
  });
